import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import NavTabs from '../components/NavTabs';

const FAQS = [
  {
    question: 'What data does this directory use?',
    answer:
      'All facility data comes from the Washington State Department of Health (WA DOH) Greenbook — the official licensed behavioral health facility registry. The admin uploads a fresh Greenbook export monthly to keep records current.',
  },
  {
    question: 'How current is the information?',
    answer:
      'Data is updated monthly when the admin uploads the latest Greenbook export. The Greenbook itself is maintained by WA DOH and reflects currently licensed facilities. Bed counts, phone numbers, and addresses reflect what is on file with DOH.',
  },
  {
    question: 'How do I search for a specific facility?',
    answer:
      'Use the search bar at the top of the main page to search by facility name. You can also filter by county, population served (adults/youth), service type (mental health, SUD, co-occurring), and care setting (inpatient, outpatient, detox).',
  },
  {
    question: 'What does "Co-occurring" mean?',
    answer:
      'A co-occurring facility serves both mental health (MH) and substance use disorder (SUD) needs. It is a useful filter when a client has a dual diagnosis and needs integrated treatment.',
  },
  {
    question: 'What is the difference between Inpatient, Outpatient, and Detox?',
    answer:
      'Inpatient facilities have licensed residential beds for 24-hour care. Outpatient facilities offer services like counseling, case management, or day programs without overnight stays. Detox (withdrawal management) facilities specialize in medically supervised substance withdrawal.',
  },
  {
    question: 'What does "MAT" mean in the filters?',
    answer:
      'MAT stands for Medication-Assisted Treatment — the use of FDA-approved medications (such as buprenorphine or methadone) combined with counseling to treat substance use disorders. Filtering by MAT shows facilities that offer an Opioid Treatment Program (OTP) certification.',
  },
  {
    question: 'Who is this tool intended for?',
    answer:
      'WA BHA Treatment Finder is built for healthcare professionals — case managers, social workers, discharge planners, crisis workers, and other clinicians — who regularly need to locate appropriate treatment placements for clients across Washington State.',
  },
  {
    question: 'How do I get full access?',
    answer:
      'Guest users can search by county only and see limited facility information. When you create a free account, you get full facility details, all filters, bed availability data, and accepting referrals status — no subscription required. Subscribe for premium features including radius search, bed alerts, notes, and the ability to save favorites. You can also use a promo code if your organization has been provided one.',
  },
  {
    question: 'How do I save a facility to my favorites?',
    answer:
      'On any facility card, click the bookmark icon in the top-right corner to save it. Your saved facilities appear on the Favorites page, accessible from the top navigation bar. Favorites are tied to your account and persist across sessions.',
  },
  {
    question: 'I found incorrect information. What should I do?',
    answer:
      'Facility data reflects what WA DOH has on file. If you believe a phone number, address, or service listing is incorrect, the most effective path is to notify the facility directly or report the discrepancy to WA DOH so it can be corrected in the source Greenbook.',
  },
];

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 pr-4">{question}</span>
        <svg
          className={`w-4 h-4 flex-shrink-0 text-gray-400 dark:text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="px-5 py-4 bg-gray-50 dark:bg-gray-700/40 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <Helmet>
        <title>FAQ & Help | WA BHA Treatment Finder</title>
        <meta name="description" content="Frequently asked questions about the WA BHA Treatment Finder — how to search, filter, and interpret facility data." />
      </Helmet>
      <Header right={null} />
      <NavTabs />

      <div className="flex-1 max-w-3xl mx-auto w-full px-4 py-10">
        <div className="mb-8">
          <Link to="/" className="text-sm text-blue-600 hover:underline flex items-center gap-1 mb-5">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to search
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">FAQ &amp; Help</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Common questions about the WA BHA Treatment Finder.
          </p>
        </div>

        <div className="space-y-3">
          {FAQS.map((faq) => (
            <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
          ))}
        </div>

        <div className="mt-10 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl px-6 py-5">
          <p className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-1">Still have questions?</p>
          <p className="text-sm text-blue-700 dark:text-blue-400">
            Email us at{' '}
            <a href="mailto:watreatmentfinder@gmail.com" className="font-medium underline hover:text-blue-900">
              watreatmentfinder@gmail.com
            </a>{' '}
            and we'll get back to you as soon as possible.
          </p>
        </div>

        <div className="mt-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl px-6 py-5">
          <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1">This tool is always growing</p>
          <p className="text-sm text-amber-700 dark:text-amber-400">
            WA BHA Treatment Finder is in active development — many more features are in the works. Have a suggestion or idea?{' '}
            <a href="mailto:watreatmentfinder@gmail.com" className="font-medium underline hover:text-amber-900 dark:hover:text-amber-200">
              Send it our way — watreatmentfinder@gmail.com
            </a>
          </p>
        </div>

        <div className="mt-4 text-center">
          <Link
            to="/why"
            className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium hover:underline"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            What makes this tool different from other directories?
          </Link>
        </div>
      </div>
    </div>
  );
}
